/**
 * Å føre blikket dit handlingen fortsetter.
 *
 * Presentasjonen av en rute ender med «planlegg denne ruta». Knappen lukket
 * dialogen og satte i gang planleggingen, men siden sto igjen på toppen — og de
 * valgene man nå skulle ta, dager, årstid og reisemåte, lå et par skjermhøyder
 * lenger ned. Man hadde bedt om å komme videre og fikk tilsynelatende ingenting.
 *
 * Rullingen er ikke pynt her: den svarer på trykket, og den viser hvor man
 * havnet. Blinket etterpå gjør det siste tydelig, for en rulling som stopper i
 * en side full av kort sier ikke i seg selv hvilket kort som var målet.
 */

/** Hvor lenge kortet blinker etter at rullingen er ferdig. Se `.card--landed`. */
const FLASH_MS = 1400;

/**
 * Hvor lenge siden må stå stille før målet regnes som landet.
 *
 * Ett bilde holder ikke: et panel over skjemaet kan vokse flere ganger mens
 * svaret kommer inn, og hver gang flytter skjemaet seg. Stillheten måles
 * derfor, den regnes ikke ut.
 */
const SETTLE_QUIET_MS = 400;

/**
 * Taket. Etter dette slippes målet uansett.
 *
 * Uten et tak ville en side som aldri faller til ro — en animasjon som går i
 * løkke, et panel som henter på nytt — holdt rullingen i gang for alltid, og
 * det er verre enn å lande feil.
 */
const SETTLE_MAX_MS = 4000;

/** Under dette er bevegelsen avrunding, ikke et panel som har endret seg. */
const SHIFT_EPSILON_PX = 1;

/**
 * Målets plass i dokumentet, uavhengig av hvor siden er rullet til.
 *
 * Nettopp derfor `+ scrollY`: tallet skal *ikke* endre seg av at vi ruller,
 * bare av at noe over målet har endret høyde. Det er den ene bevegelsen vi
 * bryr oss om, og den eneste som ødelegger landingen.
 */
function documentTop(target: Element): number {
  return target.getBoundingClientRect().top + window.scrollY;
}

/**
 * Holder målet på plass mens siden over det faller til ro.
 *
 * ── Hvorfor dette ikke kan løses med flere animasjonsrammer ────────────────
 *
 * Fordi det som flytter skjemaet, venter på nettverket. `behavior: "smooth"`
 * låser målpunktet i det øyeblikket kallet skjer, og et panel over skjemaet som
 * henter sitt eget svar vokser *etter* det — «når bør du komme?» får tolv nye
 * linjer om folkemengder i det ruta velges. Rullingen stopper da der skjemaet
 * lå, ikke der det ble. Ingen mengde rammer venter ut et API-kall.
 *
 * ── Hvorfor det observeres framfor å ramses opp ────────────────────────────
 *
 * Fordi den forrige rettingen ramset opp. App hadde en liste over de tingene
 * som flytter siden — planen som kommer, «ta over» på en delt lenke — og holdt
 * ønsket i live gjennom dem. Så ble det lagt til et panel over skjemaet som
 * henter sitt eget svar, lista ble ikke oppdatert, og feilen kom rett tilbake.
 * En liste over kjente bevegelser er en liste noen må huske å skrive på.
 *
 * Her spørres det ikke *hvorfor* siden flyttet seg. Flyttet den seg, rettes
 * landingen opp. Da gjelder rettingen også det panelet ingen har skrevet ennå.
 *
 * ── Brukeren vinner alltid ─────────────────────────────────────────────────
 *
 * Tar noen tak i siden selv, slippes målet i samme øyeblikk. En side som
 * rykker tilbake når man prøver å rulle vekk er en side som har tatt kontroll
 * fra den som bruker den, og det er en verre feil enn den vi retter.
 */
function holdAlignment(target: Element, still: boolean): void {
  if (typeof requestAnimationFrame !== "function") return;

  let previous = documentTop(target);
  let quietSince = Date.now();
  const deadline = Date.now() + SETTLE_MAX_MS;
  let frame = 0;

  const release = () => {
    cancelAnimationFrame(frame);
    // `scroll` står med vilje ikke her: rullingen vi selv ba om utløser den,
    // og da ville målet blitt sluppet i samme øyeblikk det ble satt.
    window.removeEventListener("wheel", release);
    window.removeEventListener("touchmove", release);
    window.removeEventListener("keydown", release);
  };

  window.addEventListener("wheel", release, { passive: true });
  window.addEventListener("touchmove", release, { passive: true });
  window.addEventListener("keydown", release);

  const tick = () => {
    const now = Date.now();
    const current = documentTop(target);

    if (Math.abs(current - previous) > SHIFT_EPSILON_PX) {
      previous = current;
      quietSince = now;
      // Målet flyttet seg. `scrollIntoView` er idempotent og sikter på nytt.
      target.scrollIntoView({ behavior: still ? "auto" : "smooth", block: "start" });
    }

    if (now - quietSince >= SETTLE_QUIET_MS || now >= deadline) {
      release();
      return;
    }
    frame = requestAnimationFrame(tick);
  };

  frame = requestAnimationFrame(tick);
}

/**
 * Ruller til et element og markerer det kort.
 *
 * Ingenting skjer om elementet ikke finnes. Det er tilsiktet: kalleren skal
 * kunne be om dette uten å vite om skjemaet er montert ennå, og en manglende
 * rulling er en langt mildere feil enn et kast. Men den *sier fra*: `false`
 * betyr «ankeret fantes ikke», så en kaller som venter på at skjemaet skal
 * dukke opp — en delt lenke skjuler det til mottakeren tar over — kan la
 * ønsket bli stående framfor å tro at det ble innfridd.
 *
 * `prefers-reduced-motion` respekteres. Den som har slått av bevegelse skal
 * fortsatt komme til skjemaet — bare uten den glidende turen dit.
 */
export function scrollToAnchor(id: string, flashMs: number = FLASH_MS): boolean {
  if (typeof document === "undefined") return false;
  const target = document.getElementById(id);
  if (!target) return false;

  const still =
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  /*
   * `start`, ikke `center`.
   *
   * Skjemaet er høyere enn vinduet, og et høyt element sentrert i bildet får
   * toppen skjøvet ut over kanten — man landet midt i «antall dager» uten å se
   * hvilken rute valgene gjaldt. Luften over kommer fra `scroll-margin-top`
   * på kortet, så det ikke klistrer seg til kanten.
   */
  target.scrollIntoView({ behavior: still ? "auto" : "smooth", block: "start" });

  // Og bli der, om noe over skjemaet vokser mens svaret kommer inn.
  holdAlignment(target, still);

  /*
   * Blinket legges på etter at rullingen har begynt, og tas av igjen etterpå.
   * Klassen må fjernes, ellers kan den ikke legges på en gang til — en klasse
   * som alt står der utløser ingen ny animasjon.
   */
  target.classList.remove("card--landed");
  // Tvinger fram en ny layout, slik at nettleseren ser at klassen forsvant.
  void target.offsetWidth;
  target.classList.add("card--landed");
  window.setTimeout(() => target.classList.remove("card--landed"), flashMs);
  return true;
}

/** Skjemaet der reisen settes opp. Se `id` på kortet i ControlPanel. */
export function scrollToForm(): boolean {
  return scrollToAnchor("plan-form");
}

/**
 * Selve planen — dagene, ikke skjemaet som ba om dem.
 *
 * ── Hvorfor de to er forskjellige mål ──────────────────────────────────────
 *
 * Meldt fra bruk: «velger jeg Trolltunga, hvorfor lander jeg her?» med et
 * skjermbilde av *skjemaet*, og deretter «den skal lande meg rett på ruta».
 *
 * Og det er riktig. Å velge en rute er å begynne på et spørsmål — da er
 * skjemaet svaret, for det er der resten av spørsmålet stilles. Å velge ett
 * sted er å være ferdig med å spørre: planen finnes allerede når man lander,
 * og å bli sluppet av ved skjemaet er å bli bedt om å fylle ut noe man nettopp
 * har svart på.
 *
 * Returnerer `false` til tavla faktisk finnes, slik at ønsket kan stå til
 * planen er hentet. Se `wants` i App.tsx.
 */
export function scrollToPlan(): boolean {
  return scrollToAnchor("plan");
}
